import React from "react";
import { Tooltip, Typography } from "antd";

const { Text, Paragraph } = Typography;

export type DayCount = {
  date: string;
  total: number;
  errors: number;
};

function barColor(day: DayCount): string {
  if (day.total === 0) return "#f0f0f0";
  if (day.errors > 0) return "#ff4d4f";
  return "#52c41a";
}

export function dayLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

type DailyLogStripProps = {
  days: DayCount[];
  truncated: boolean;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
};

/** One bar per day for the last 30 days. Click a bar to load that day. */
export const PartnerApiLogsDailyStrip: React.FC<DailyLogStripProps> = ({
  days,
  truncated,
  selectedDate,
  onSelectDate,
}) => {
  const max = Math.max(1, ...days.map((day) => day.total));
  const total = days.reduce((sum, day) => sum + day.total, 0);
  const first = days[0]?.date;
  const last = days[days.length - 1]?.date;

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Text type="secondary">Requests per day, last 30 days</Text>
        <Text type="secondary">{total.toLocaleString("en-GB")} total</Text>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 3,
          height: 48,
        }}
      >
        {days.map((day) => {
          const height =
            day.total === 0
              ? 4
              : Math.max(8, Math.round((day.total / max) * 48));
          const selected = day.date === selectedDate;
          return (
            <Tooltip
              key={day.date}
              title={`${dayLabel(day.date)} · ${day.total} request${day.total === 1 ? "" : "s"}${
                day.errors > 0
                  ? ` · ${day.errors} error${day.errors === 1 ? "" : "s"}`
                  : ""
              } · click to load`}
            >
              <button
                type="button"
                aria-label={`Load logs for ${dayLabel(day.date)}`}
                aria-pressed={selected}
                onClick={() => onSelectDate(day.date)}
                style={{
                  flex: 1,
                  height,
                  padding: 0,
                  border: "none",
                  outline: selected ? "2px solid #1677ff" : "none",
                  outlineOffset: 1,
                  background: barColor(day),
                  borderRadius: 2,
                  minWidth: 0,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 4,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {first ? dayLabel(first) : ""}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {last ? dayLabel(last) : ""}
        </Text>
      </div>
      {truncated && (
        <Paragraph type="warning" style={{ margin: "8px 0 0", fontSize: 12 }}>
          More than 20,000 requests in this window — showing the latest ones;
          older day counts may be incomplete.
        </Paragraph>
      )}
    </div>
  );
};
